import React, { useState, useRef, useEffect } from 'react';
import { Bookmark, X } from 'lucide-react';
import { useDocumentProgress } from '../../../contexts/DocumentProgressContext';

interface PageBookmarkButtonProps {
  documentId: string;
  caseId: string;
  pageNumber: number;
}

export const PageBookmarkButton: React.FC<PageBookmarkButtonProps> = ({
  documentId,
  caseId,
  pageNumber,
}) => {
  const {
    isPageBookmarked,
    getBookmarkForPage,
    addBookmark,
    removeBookmark,
    updateBookmarkNote,
  } = useDocumentProgress();

  const [showPopover, setShowPopover] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const bookmarked = isPageBookmarked(documentId, pageNumber);
  const existingBookmark = getBookmarkForPage(documentId, pageNumber);

  // Sync note state when popover opens or bookmark changes
  useEffect(() => {
    if (showPopover && existingBookmark) {
      setNote(existingBookmark.note ?? '');
    }
  }, [showPopover, existingBookmark]);

  // Close popover on outside click
  useEffect(() => {
    if (!showPopover) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setShowPopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopover]);

  const handleClick = async () => {
    if (bookmarked) {
      // Already bookmarked: show popover
      setShowPopover((prev) => !prev);
    } else {
      // Not bookmarked: add one
      await addBookmark(documentId, caseId, pageNumber);
      setShowPopover(true);
    }
  };

  const handleSaveNote = async () => {
    if (!existingBookmark) return;
    setSaving(true);
    try {
      await updateBookmarkNote(documentId, existingBookmark.id, note);
      setShowPopover(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingBookmark) return;
    await removeBookmark(documentId, existingBookmark.id);
    setShowPopover(false);
  };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        onClick={handleClick}
        className="p-1.5 rounded-md text-medical-500 hover:text-medical-700 hover:bg-medical-200 dark:text-medical-400 dark:hover:text-medical-200 dark:hover:bg-medical-700 transition-colors"
        title={bookmarked ? 'Edit bookmark' : 'Bookmark this page'}
      >
        {bookmarked ? (
          <Bookmark className="h-4 w-4 fill-amber-500 text-amber-500" />
        ) : (
          <Bookmark className="h-4 w-4" />
        )}
      </button>

      {/* Popover */}
      {showPopover && existingBookmark && (
        <div className="absolute top-full right-0 mt-1 z-50 w-56 rounded-lg border border-medical-200 dark:border-medical-700 bg-white dark:bg-medical-800 shadow-lg">
          <div className="p-2.5">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-medium text-medical-500 dark:text-medical-400">
                Page {pageNumber} bookmark
              </span>
              <button
                onClick={() => setShowPopover(false)}
                className="p-0.5 rounded text-medical-400 hover:text-medical-600 dark:hover:text-medical-300"
              >
                <X className="h-3 w-3" />
              </button>
            </div>

            {/* Note textarea */}
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={200}
              rows={3}
              placeholder="Add a note..."
              className="w-full text-xs rounded-md border border-medical-200 dark:border-medical-600 bg-medical-50 dark:bg-medical-900 text-medical-700 dark:text-medical-200 placeholder-medical-400 px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
            <p className="text-[9px] text-medical-400 text-right mt-0.5">
              {note.length}/200
            </p>

            {/* Actions */}
            <div className="flex items-center justify-between mt-2">
              <button
                onClick={handleDelete}
                className="px-2 py-1 text-[10px] font-medium rounded border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
              >
                Delete bookmark
              </button>
              <button
                onClick={handleSaveNote}
                disabled={saving}
                className="px-2 py-1 text-[10px] font-medium rounded bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
